import { useTranslation } from 'react-i18next';
import { Loader2, CircleCheck, OctagonX } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FieldGroup, FieldSet } from '@/components/ui/field';

import { useGetUserContextQuery } from '@/entities/layout';
import { FormInput } from '@/shared/ui/form';

import { useChangePasswordForm } from '../model';

interface ChangePasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangePasswordModal = ({ open, onOpenChange }: ChangePasswordModalProps) => {
  const { t } = useTranslation();
  const { data: userContext } = useGetUserContextQuery();

  const { form, onSubmit, alertState, isLoading } = useChangePasswordForm({ open });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty }
  } = form;

  const user = userContext?.user;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t('pref.changepassword.label')}</DialogTitle>
        </DialogHeader>

        {alertState && (
          <Alert variant={alertState.type === 'error' ? 'destructive' : 'success'} className="mb-4">
            {alertState.type === 'error' ? <OctagonX className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
            <AlertTitle>{t(alertState.type === 'error' ? 'common.error' : 'common.success')}</AlertTitle>
            <AlertDescription>{alertState.message}</AlertDescription>
          </Alert>
        )}

        {/* Read-only profile fields matching expectations */}
        <FieldSet>
          <FieldGroup className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-4 border-b border-border mb-4">
            <FormInput label={t('user.org.label')} value={user?.organizationName} readOnly />{' '}
            <FormInput label={t('user.email.label')} value={user?.email} readOnly />
            <FormInput label={t('user.name.label')} value={user?.name} readOnly />
          </FieldGroup>
        </FieldSet>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FieldGroup>
            <FormInput
              id="oldPassword"
              type="password"
              label={t('pref.oldpassword.label')}
              placeholder={t('pref.oldpassword.desc')}
              registration={register('oldPassword')}
              error={errors.oldPassword?.message}
              required
            />

            <FormInput
              id="newPassword"
              type="password"
              label={t('pref.newpassword.label')}
              placeholder={t('pref.newpassword.desc')}
              registration={register('newPassword')}
              error={errors.newPassword?.message}
              required
            />

            <FormInput
              id="retypePassword"
              type="password"
              label={t('pref.retype.label')}
              placeholder={t('pref.retype.desc')}
              registration={register('retypePassword')}
              error={errors.retypePassword?.message}
              required
            />
          </FieldGroup>

          <DialogFooter className="mt-6">
            <ActionButton type="button" intent="dismiss" onClick={() => onOpenChange(false)}>
              {t('gen.closedialog.label')}
            </ActionButton>
            <ActionButton intent="primary" type="submit" disabled={isLoading || !isDirty || !isValid}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('newcmr.save.label')}
            </ActionButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
